import { useMemo, useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { IntakeHubDocumentInventoryItemResponse } from '@store/generated/intake-hub';

import { itemIsOpen, sortIntakeItemsByStatus } from '../domain';
import type {
  IntakeBriefingAction,
  IntakeDashboardScenario,
  IntakeItem,
  IntakeItemType,
  IntakeServiceId,
} from '../types';

import { IntakeBriefingCard } from './IntakeBriefingCard';
import { IntakeDocumentInventorySection } from './IntakeDocumentInventorySection';
import { type IntakeDocumentAccess, IntakeItemDetailsDialog } from './IntakeItemDetailsDialog';
import { IntakeItemListSection } from './IntakeItemListSection';
import { PayerStatusCard } from './PayerStatusCard';
import { ProviderProfileSection } from './ProviderProfileSection';
import { ServiceProgressRing } from './ServiceProgressRing';

const MAX_BRIEFING_ITEMS = 4;

interface IntakeHubDashboardProps {
  scenario: IntakeDashboardScenario;
  documentInventory?: IntakeHubDocumentInventoryItemResponse[];
  documentAccess?: IntakeDocumentAccess;
  onUploadRequest?: (item?: IntakeItem) => void;
  onFolderUploadRequest?: () => void;
}

export function IntakeHubDashboard({
  scenario,
  documentInventory = [],
  documentAccess,
  onUploadRequest,
  onFolderUploadRequest,
}: IntakeHubDashboardProps) {
  const { intakeHub } = useEventTracking();
  const [activeServiceId, setActiveServiceId] = useState<IntakeServiceId>(scenario.activeServiceId);
  const [detailsItem, setDetailsItem] = useState<IntakeItem | null>(null);
  const [instructionItem, setInstructionItem] = useState<IntakeItem | null>(null);
  const activeService = scenario.services.find(service => service.id === activeServiceId);
  const serviceLabels = useMemo(
    () =>
      scenario.services.reduce(
        (labels, service) => ({ ...labels, [service.id]: service.label }),
        {} as Record<IntakeServiceId, string>,
      ),
    [scenario.services],
  );
  const serviceItems = useMemo(
    () => scenario.serviceItems[activeServiceId] ?? [],
    [activeServiceId, scenario.serviceItems],
  );
  const briefingActions = useMemo(
    () => buildOpenBriefingActions(scenario, serviceLabels),
    [scenario, serviceLabels],
  );
  const serviceItemsByType = useMemo(() => groupServiceItems(serviceItems), [serviceItems]);
  const payerStatuses = scenario.payerStatuses[activeServiceId] ?? [];
  const handleBriefingAction = (action: IntakeBriefingAction) => {
    const item = action.item;
    if (!item) return;
    handleDefaultItemAction(item, {
      onUploadRequest,
      openDetails: setDetailsItem,
      openInstructions: setInstructionItem,
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: ELIG_TOKENS.bg,
        color: 'text.primary',
      }}
    >
      <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 3 } }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' } }}
          >
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                Intake Hub
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.25 }}>
                {scenario.provider.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {scenario.provider.credentialType} · {scenario.provider.subtitle}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<UploadFileOutlinedIcon />}
                onClick={() => onUploadRequest?.()}
              >
                Upload files
              </Button>
              {onFolderUploadRequest && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CreateNewFolderOutlinedIcon />}
                  onClick={() => {
                    intakeHub.folderUploadClicked({ surface: 'dashboard' });
                    onFolderUploadRequest();
                  }}
                >
                  Upload folder
                </Button>
              )}
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Stack spacing={2} sx={{ minHeight: 0, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                  alignItems: 'stretch',
                }}
              >
                <IntakeBriefingCard
                  actions={briefingActions}
                  serviceLabels={serviceLabels}
                  onAction={handleBriefingAction}
                />
                <ProviderProfileSection
                  provider={scenario.provider}
                  documentAccess={documentAccess}
                  onUploadRequest={onUploadRequest}
                />
              </Box>

              <Box
                sx={{
                  p: { xs: 1.5, sm: 1.75 },
                  borderRadius: '8px',
                  bgcolor: ELIG_TOKENS.card,
                  border: `1px solid ${ELIG_TOKENS.lineStrong}`,
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  sx={{ justifyContent: 'space-around', alignItems: 'center' }}
                >
                  {scenario.services.map(service => (
                    <ServiceProgressRing key={service.id} service={service} />
                  ))}
                </Stack>
              </Box>

              <Box
                sx={{
                  borderRadius: '8px',
                  bgcolor: ELIG_TOKENS.card,
                  border: `1px solid ${ELIG_TOKENS.lineStrong}`,
                  overflow: 'hidden',
                  minHeight: 0,
                }}
              >
                <Tabs
                  value={activeServiceId}
                  onChange={(_event, value: IntakeServiceId) => setActiveServiceId(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    px: 1.5,
                    borderBottom: `1px solid ${ELIG_TOKENS.line}`,
                    minHeight: 44,
                    '& .MuiTab-root': {
                      minHeight: 44,
                      color: ELIG_TOKENS.inkSoft,
                      fontWeight: 650,
                      textTransform: 'none',
                    },
                    '& .Mui-selected': {
                      color: ELIG_TOKENS.midnight,
                    },
                    '& .MuiTabs-indicator': {
                      bgcolor: ELIG_TOKENS.midnight,
                    },
                  }}
                >
                  {scenario.services.map(service => (
                    <Tab
                      key={service.id}
                      value={service.id}
                      label={`${service.label} · ${service.progress}%`}
                    />
                  ))}
                </Tabs>

                <Box sx={{ p: { xs: 1.5, sm: 2 }, minHeight: 0 }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', md: 'center' },
                      mb: 1.5,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                        {activeService?.label ?? 'Service'} workspace
                      </Typography>
                      <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft }}>
                        Service tasks and required information.
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    {SERVICE_GROUPS.map(group => (
                      <ServiceItemGroup
                        key={group.type}
                        title={group.title}
                        items={serviceItemsByType[group.type]}
                        documentAccess={documentAccess}
                        onUploadRequest={onUploadRequest}
                      />
                    ))}

                    {payerStatuses.length > 0 && (
                      <Stack spacing={0.75} sx={{ minHeight: 0, minWidth: 0 }}>
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{ fontWeight: 900 }}
                        >
                          Payers
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: 'repeat(2, minmax(0, 1fr))',
                              md: 'repeat(3, minmax(0, 1fr))',
                            },
                            gap: 0.9,
                          }}
                        >
                          {payerStatuses.map(payer => (
                            <PayerStatusCard key={payer.id} payer={payer} />
                          ))}
                        </Box>
                      </Stack>
                    )}
                  </Box>
                </Box>
              </Box>
            </Stack>

            <IntakeDocumentInventorySection
              documents={documentInventory}
              documentAccess={documentAccess}
            />
          </Box>
        </Stack>
      </Box>
      <InstructionDialog item={instructionItem} onClose={() => setInstructionItem(null)} />
      {detailsItem && (
        <IntakeItemDetailsDialog
          open
          item={detailsItem}
          documentAccess={documentAccess}
          onClose={() => setDetailsItem(null)}
        />
      )}
    </Box>
  );
}

const SERVICE_GROUPS: Array<{ type: IntakeItemType; title: string }> = [
  { type: 'information', title: 'Information' },
  { type: 'document', title: 'Documents' },
  { type: 'action', title: 'Actions' },
  { type: 'staff_task', title: 'JotPsych work' },
];

function groupServiceItems(items: IntakeItem[]): Record<IntakeItemType, IntakeItem[]> {
  return items.reduce<Record<IntakeItemType, IntakeItem[]>>(
    (groups, item) => {
      groups[item.type] = [...(groups[item.type] ?? []), item];
      return groups;
    },
    {
      information: [],
      document: [],
      action: [],
      staff_task: [],
      review: [],
    },
  );
}

function buildOpenBriefingActions(
  scenario: IntakeDashboardScenario,
  serviceLabels: Record<IntakeServiceId, string>,
): IntakeBriefingAction[] {
  const itemsById = new Map<string, IntakeItem>();

  for (const item of [
    ...scenario.provider.neededItems,
    ...scenario.provider.onFileItems,
    ...Object.values(scenario.serviceItems).flat(),
  ]) {
    itemsById.set(item.intakeItemId ?? item.id, item);
  }

  return sortIntakeItemsByStatus(Array.from(itemsById.values()))
    .filter(itemIsOpen)
    .slice(0, MAX_BRIEFING_ITEMS)
    .map(item => {
      const serviceId = item.serviceIds[0] ?? scenario.activeServiceId;
      return {
        id: item.intakeItemId ?? item.id,
        title: item.label,
        description: item.issueNote ?? item.description ?? serviceLabels[serviceId] ?? '',
        serviceId,
        tone:
          item.status === 'needs_attention' || item.status === 'ai_flagged'
            ? 'error'
            : item.status === 'expiring'
              ? 'warning'
              : 'default',
        icon: getBriefingIcon(item),
        actionLabel: item.actionLabel ?? getBriefingActionLabel(item),
        item,
      };
    });
}

function handleDefaultItemAction(
  item: IntakeItem,
  handlers: {
    onUploadRequest?: (item?: IntakeItem) => void;
    openDetails: (item: IntakeItem) => void;
    openInstructions: (item: IntakeItem) => void;
  },
) {
  const isJotPsychOwned = item.owner === 'jotpsych' || item.type === 'staff_task';
  if (isJotPsychOwned) return;
  if (item.type === 'document' && handlers.onUploadRequest) {
    handlers.onUploadRequest(item);
    return;
  }
  if (item.type === 'information' || itemHasEvidence(item)) {
    handlers.openDetails(item);
    return;
  }
  if (item.instruction) {
    handlers.openInstructions(item);
  }
}

function itemHasEvidence(item: IntakeItem) {
  const extractedFields =
    item.evidenceSummary.extracted_fields &&
    typeof item.evidenceSummary.extracted_fields === 'object'
      ? (item.evidenceSummary.extracted_fields as Record<string, unknown>)
      : {};
  return (
    item.sourceRefs.length > 0 ||
    Object.keys(extractedFields).length > 0 ||
    Boolean(item.evidenceSummary.value_preview) ||
    Boolean(item.evidenceSummary.label)
  );
}

function getBriefingIcon(item: IntakeItem) {
  if (
    item.status === 'needs_attention' ||
    item.status === 'ai_flagged' ||
    item.status === 'expiring'
  ) {
    return ErrorOutlineOutlinedIcon;
  }
  if (
    item.status === 'submitted' ||
    item.status === 'in_review' ||
    item.status === 'staff_in_progress'
  ) {
    return AccessTimeOutlinedIcon;
  }
  if (item.type === 'document') {
    return UploadFileOutlinedIcon;
  }
  if (item.type === 'information') {
    return EditNoteOutlinedIcon;
  }
  return RadioButtonUncheckedOutlinedIcon;
}

function getBriefingActionLabel(item: IntakeItem) {
  if (item.type === 'document') {
    if (item.status === 'submitted' || item.status === 'in_review') return 'Replace';
    return 'Upload';
  }
  if (item.type === 'information') return 'Add info';
  if (item.instruction) return 'How to find';
  if (item.owner === 'jotpsych') return 'In progress';
  return 'Open';
}

function InstructionDialog({ item, onClose }: { item: IntakeItem | null; onClose: () => void }) {
  return (
    <Dialog
      open={Boolean(item)}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2.5,
          border: `1px solid ${ELIG_TOKENS.lineStrong}`,
        },
      }}
    >
      {item && (
        <DialogContent sx={{ p: 0 }}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              px: 2.25,
              py: 2,
              borderBottom: `1px solid ${ELIG_TOKENS.line}`,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>
                How to find
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, fontSize: 18 }}>
                {item.label}
              </Typography>
            </Box>
            <IconButton size="small" aria-label="Close instructions" onClick={onClose}>
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Box sx={{ px: 2.25, py: 2.25 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
              {item.instruction}
            </Typography>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}

function ServiceItemGroup({
  title,
  items,
  documentAccess,
  onUploadRequest,
}: {
  title: string;
  items: IntakeItem[];
  documentAccess?: IntakeDocumentAccess;
  onUploadRequest?: (item?: IntakeItem) => void;
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <IntakeItemListSection
      title={title}
      items={items}
      emptyLabel="Nothing matches this filter."
      documentAccess={documentAccess}
      onUploadRequest={onUploadRequest}
    />
  );
}
